import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

import {HERO_BANNER_APPEARANCE} from '@amblique/king-living/app/contentful/models/hero-banner/hero-banner-appearance';

const HERO_MOBILE_MQ = '@media screen and (max-width: 991px)';

/** Mobile hero natural-ratio video (king-living layer class; no vendor size-contain hook) */
const HERO_NATURAL_VIDEO_LAYER =
  '.hero-banner__video-layer--mobile.hero-banner__video-layer--natural-ratio';

/**
 * Shared mobile natural-ratio video styles for fill heroes (CF-01 / CF-04 / CF-024 / CF-06).
 * @param {string} modifierClass - BEM modifier on the hero root (or descendant when scope is descendant)
 * @param {Object} extraMobileRules - Additional rules merged into the mobile block
 * @param {{ scope?: 'self' | 'descendant', hideContent?: boolean }} [options]
 */
function createHeroVideoNoContentMobileSx(modifierClass, extraMobileRules = {}, options = {}) {
  const {scope = 'self', hideContent = true} = options;
  const root = scope === 'descendant' ? `& .${modifierClass}` : `&.${modifierClass}`;

  return {
    [HERO_MOBILE_MQ]: {
      [root]: {
        minHeight: 'auto !important',
      },
      [`${root} .hero-image-wrapper`]: {
        bottom: 'auto',
        left: 'auto',
        position: 'static',
        right: 'auto',
        top: 'auto',
      },
      ...(hideContent ?
        {
          [`${root} .hero-content-wrapper`]: {
            display: 'none',
          },
        } :
        {}),
      [`${root} .hero-banner__video-layer--mobile`]: {
        width: '100%',
      },
      [`${root} .hero-banner__video-layer--mobile .component--video`]: {
        width: '100%',
      },
      [`${root} .hero-banner__video-layer--mobile .amb-video`]: {
        position: 'relative',
        width: '100%',
      },
      [`${root} ${HERO_NATURAL_VIDEO_LAYER} .amb-video > iframe`]: {
        height: '100% !important',
        left: '0 !important',
        margin: 0,
        position: 'absolute',
        top: '0 !important',
        transform: 'none !important',
        width: '100% !important',
      },
      [`${root} ${HERO_NATURAL_VIDEO_LAYER} .amb-video > video`]: {
        height: '100%',
        left: 0,
        objectFit: 'contain',
        position: 'absolute',
        top: 0,
        width: '100%',
      },
      [`${root} .hero-banner__video-layer--mobile .hero-banner-native-video`]: {
        position: 'relative',
        width: '100%',
      },
      [`${root} .hero-banner__video-layer--mobile .hero-banner-native-video video`]: {
        display: 'block',
        height: 'auto',
        objectFit: 'contain',
        position: 'relative',
        width: '100%',
      },
      ...extraMobileRules,
    },
  };
}

const heroCf01VideoNoContentMobileSx = createHeroVideoNoContentMobileSx(
  'hero-banner--cf01-video-no-content'
);

const heroCf04VideoMobileSx = createHeroVideoNoContentMobileSx(
  'hero-banner--cf04-video-no-content',
  {
    '&.hero-banner--cf04-video-with-content .amb-video.landscape': {
      height: '100%',
      left: '50%',
      position: 'absolute',
      top: 0,
      transform: 'translateX(-50%)',
      width: 'auto',
    },
  }
);

const heroCf024VideoNoContentSx = createHeroVideoNoContentMobileSx(
  'hero-banner--cf024-video-no-content',
  {
    '&.hero-banner--cf024-video-no-content .hero-image-wrapper::after': {
      display: 'none',
    },
  }
);

/** CF-023 no-content (mobile) — in-flow media; hide content; root max 520px */
const heroCf023NoContentSx = {
  [HERO_MOBILE_MQ]: {
    ...createHeroVideoNoContentMobileSx('hero-banner--cf023-no-content', {}, {scope: 'descendant'})[
      HERO_MOBILE_MQ
    ],
    '&:has(.hero-banner--cf023-no-content)': {
      maxWidth: '520px',
      width: '100%',
    },
  },
};

/** CF-023 with-content (mobile) — in-flow media; body copy overlays video */
const heroCf023WithContentMobileSx = createHeroVideoNoContentMobileSx(
  'hero-banner--cf023-with-content',
  {
    '& .hero-banner--cf023-with-content .hero-content-wrapper': {
      bottom: 0,
      display: 'flex',
      left: 0,
      position: 'absolute',
      right: 0,
      top: 0,
      zIndex: 2,
    },
    '& .hero-banner--cf023-with-content .hero-banner__video-layer--mobile .amb-video': {
      height: 'auto !important',
    },
  },
  {scope: 'descendant', hideContent: false}
);

/** CF-06 no-content (mobile only) — media uses natural aspect ratio */
const heroCf06NoContentSx = createHeroVideoNoContentMobileSx('hero-banner--cf06-no-content', {
  '&.hero-banner--cf06-no-content .hero-image-wrapper': {
    aspectRatio: 'auto',
    height: 'auto',
  },
});

const heroBackgroundMedia = {
  width: '100%',
  height: '100%',
  sx: {
    'img, video': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
};

/** Defaults to enabled when the CMS field is absent (legacy entries). */
function isHeroBackgroundGradientEnabled(component) {
  return component?.backgroundGradient !== false;
}

/** Absolute-fill background layer with optional gradient overlay for text contrast. */
function createHeroBackgroundWrapper(gradientOverlay, enabled = true) {
  const wrapper = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  };

  if (!enabled || !gradientOverlay) {
    return wrapper;
  }

  return {
    ...wrapper,
    _after: {
      content: '""',
      position: 'absolute',
      inset: 0,
      background: gradientOverlay,
      zIndex: 1,
    },
  };
}

/** Relative media crop box (CF-06). */
function createHeroMediaWrapper(overrides = {}) {
  return {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    ...overrides,
  };
}

const helpers = createMultiStyleConfigHelpers([
  'root',
  'grid',
  'mediaRegion',
  'contentRegion',
  'body',
  'cta',
  'media',
  // Hero Banner Block (CF-01) parts
  'wrapper',
  'backgroundWrapper',
  'backgroundMedia',
  'contentWrapper',
  'contentWrapperOuter',
  'title',
  'subtitle',
  'actionLinks',
  // Hero Banner Block (CF-04) parts
  'bottomBar',
  'headingIcon',
  'leftCta',
  'rightCta',
  // Hero Banner Block (CF-06) parts
  'mediaWrapper',
  'contentSection',
  'titleColumn',
  'subtitleColumn',
  // Range scroll secondary nav parts
  'secondaryNav',
  'secondaryNavContent',
  'secondaryNavTitle',
  'secondaryNavCtas'
]);

/**
 * @typedef {import('@chakra-ui/styled-system').SystemStyleObject} SystemStyleObject
 * @typedef {import('@chakra-ui/styled-system').PartsStyleInterpolation<{
 *  keys: ("root" | "grid" | "mediaRegion" | "contentRegion" | "body" | "cta")[]
 * }>} HeroBannerStyles
 */
export const heroBannerStyles = helpers.defineMultiStyleConfig({
  /**
   * @param {Object} args - Component arguments
   * @param {Contentful.HeroBanner} args.component - HeroBanner component data
   * @param {boolean} [args.isComplete] - Whether component has all fields
   * @param {boolean} [args.isFetching] - Whether additional data is being fetched
   *
   * @returns {HeroBannerStyles} - Component styles
   */
  baseStyle: ({component}) => {
    return {
      root: {
        backgroundImage: '',
      },

      secondaryNav: {
        backdropFilter: {base: 'none', lg: 'blur(10px)'},
        bg: {base: 'white', lg: 'whiteAlpha.800'},
        borderColor: 'grayAlpha.10',
        bottom: {base: 0, lg: 'auto'},
        left: 0,
        paddingBottom: {base: 'max(env(safe-area-inset-bottom), 12px)', lg: '15px'},
        paddingLeft: {base: '24px', lg: '48px'},
        paddingRight: {base: '24px', lg: '32px'},
        paddingTop: {base: '12px', lg: '15px'},
        position: 'fixed',
        right: 0,
        transition: 'top 0.2s ease, transform 0.2s ease, opacity 0.2s ease',
        width: '100%',
        zIndex: 'sticky',
      },

      secondaryNavContent: {
        alignItems: 'center',
        gap: 0,
        justifyContent: 'space-between',
        width: '100%',
      },

      secondaryNavTitle: {
        color: 'grayAlpha.90',
        flex: '1 0 0',
        fontSize: {base: '16px', lg: '20px'},
        fontWeight: {base: 'semibold', lg: 'medium'},
        lineHeight: 1.2,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },

      secondaryNavCtas: {
        alignItems: 'center',
        flexShrink: 0,
        gap: 0,
        sx: {
          // Shared base reset for both buttons
          '& .hero-banner__secondary-nav-cta .link, & .hero-banner__secondary-nav-cta .link .chakra-text': {
            fontSize: '14px',
            fontWeight: '600',
          },
          '& .hero-banner__secondary-nav-cta .link': {
            alignItems: 'center',
            display: 'inline-flex',
            justifyContent: 'center',
            height: '54px',
            whiteSpace: 'nowrap',
          },
          // Left button — outlined dark
          '& .hero-banner__secondary-nav-cta .link--outlined-dark': {
            backgroundColor: 'transparent',
            borderColor: 'brandColors.greyscale90',
            borderStyle: 'solid',
            borderWidth: '1px',
            color: 'brandColors.greyscale',
            paddingLeft: {base: '16px', lg: '48px'},
            paddingRight: {base: '16px', lg: '48px'},
            minWidth: '160px',
          },
          '& .hero-banner__secondary-nav-cta .link--outlined-dark:hover': {
            backgroundColor: 'brandColors.greyscale10',
          },
          // Right button — filled dark
          '& .hero-banner__secondary-nav-cta .link--filled-dark': {
            backgroundColor: 'brandColors.greyscale',
            borderColor: 'brandColors.greyscale',
            borderWidth: '0',
            color: 'white',
            paddingLeft: {base: '16px', lg: '32px'},
            paddingRight: {base: '16px', lg: '32px'},
          },
          '& .hero-banner__secondary-nav-cta .link--filled-dark img': {
            filter: 'brightness(0) invert(1)',
          },
          '& .hero-banner__secondary-nav-cta .link--filled-dark:hover': {
            backgroundColor: 'brandColors.greyscale80',
          },
          // Mobile: hide text in right button — icon only
          '& .hero-banner__secondary-nav-cta .link--icon-on-left .chakra-text': {
            display: {base: 'none', lg: 'block'},
          },
        },
      },
    };
  },
  variants: {
    /**
     * @param {Object} args - Component arguments
     * @param {Contentful.HeroBanner} args.component - HeroBanner component data
     * @param {boolean} [args.isComplete] - Whether component has all fields
     * @param {boolean} [args.isFetching] - Whether additional data is being fetched
     *
     * @returns {HeroBannerStyles} - Component styles
     */
    [HERO_BANNER_APPEARANCE.CALLOUT_SPLIT_CF_023]: (args) => {
      const {body, cta} = args?.component || {};
      const isNoContent = !body && !cta;

      return {
        root: {
          padding: {base: '20px 0', lg: '0 80px'},
          maxWidth: {base: '100%', lg: '1440px'},
          marginX: 'auto',
          width: '100%',
          ...(isNoContent ?
            {sx: heroCf023NoContentSx} :
            {sx: heroCf023WithContentMobileSx}),
        },

        grid: {
          templateColumns: {base: '1fr', lg: '1fr 1fr'},
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
        },

        contentRegion: {
          background: {
            base: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.50) 44.28%)',
            lg: 'rgba(27, 26, 24, 0.90)',
          },
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
          gap: {base: '30px', lg: '24px'},
          justifyContent: {base: 'center', lg: 'flex-end'},
          padding: {base: '40px 20px', lg: '48px 72px 48px 48px'},
          zIndex: 2,
          sx: {
            '.link--outlined-dark': {
              'color': 'brandColors.primaryLinen100',
              'borderColor': 'brandColors.primaryLinen100',
              '& p': {
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: '600',
                lineHeight: '120%',
              },
            },
            '& .component--action-links .chakra-stack': {
              justifyContent: {base: 'center', lg: 'flex-start'},
            },
          },
        },

        // Mobile in-flow + desktop fill: scoped via cf023-with-content / cf023-no-content on root sx.
        mediaRegion: {
          height: {base: 'auto', lg: '100%'},
          overflow: 'hidden',
          position: 'relative',
          width: '100%',
          zIndex: 1,
          sx: {
            '@media screen and (min-width: 992px)': {
              '.hero-banner__video-layer': {
                height: '100%',
                width: '100%',
              },
              '.component--video': {
                height: '100%',
                width: '100%',
              },
              '.amb-video': {
                aspectRatio: 'unset',
                height: '100%',
                width: '100%',
              },
            },
          },
        },

        media: {
          ...heroBackgroundMedia,
          height: {base: 'auto', lg: '100%'},
        },

        body: {
          sx: {
            '.amb-markup': {
              'display': 'flex',
              'flexDirection': 'column',
              'gap': '16px',
              'textAlign': {base: 'center', lg: 'left'},

              'h1, h2, h3, h4, h5, h6': {
                color: 'brandColors.primaryLinen100',
                fontSize: '10px',
                fontWeight: '600',
                letterSpacing: '2px',
                lineHeight: '120%',
                marginBottom: 0,
                textTransform: 'uppercase',
              },

              '.chakra-text': {
                fontSize: {base: '20px', lg: '28px'},
                fontWeight: '500',
                letterSpacing: '-0.28px',
                lineHeight: '120%',

                _last: {
                  marginBottom: 0,
                },
              },
            },
          },
        },

        cta: {},
      };
    },

    /**
     * Callout Block - Overlay (CF-024)
     * Full-width background image/video with centered subtitle and CTA overlay
     *
     * @param {Object} args - Component arguments
     * @param {Contentful.HeroBanner} args.component - HeroBanner component data
     * @param {boolean} [args.isComplete] - Whether component has all fields
     * @param {boolean} [args.isFetching] - Whether additional data is being fetched
     *
     * @returns {HeroBannerStyles} - Component styles
     */
    [HERO_BANNER_APPEARANCE.CALLOUT_OVERLAY_CF_024]: (args) => ({
      root: {
        padding: {base: '60px 20px', lg: '120px 80px'},
        maxWidth: {base: '100%', lg: '1440px'},
        marginX: 'auto',
        width: '100%',
      },
      wrapper: {
        position: 'relative',
        width: '100%',
        minHeight: {base: '400px', lg: '600px'},
        overflow: 'hidden',
        sx: {
          ...heroCf024VideoNoContentSx,
          '& .chakra-heading': {
            letterSpacing: {base: '0', lg: '-0.4px'},
            fontWeight: {base: '500', lg: '500'},
            lineHeight: {base: '150%', lg: '120%'},
          },
        },
      },

      backgroundWrapper: createHeroBackgroundWrapper(
        'linear-gradient(180deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.40) 100%)'
      ),

      backgroundMedia: heroBackgroundMedia,

      contentWrapper: {
        position: 'relative',
        zIndex: 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        height: '100%',
        minHeight: {base: '400px', lg: '600px'},
        padding: {base: '40px 32px', md: '60px 80px', lg: '80px 120px'},
        gap: {base: '24px', lg: '32px'},
      },

      subtitle: {
        color: 'white',
        fontFamily: 'heading',
        fontSize: {base: '24px', lg: '40px'},
        fontWeight: '500',
        lineHeight: '120%',
        letterSpacing: '-0.02em',
        margin: 0,
        maxWidth: {base: '100%', lg: '600px'},
      },

      actionLinks: {
        marginTop: {base: '0', lg: '12px'},
        sx: {
          '.chakra-stack': {
            justifyContent: 'center',
          },
          '&:hover': {
            '& .chakra-link, & button, & a, & .chakra-link p': {
              textDecoration: 'none',
            },
          },
          '& .chakra-link': {
            padding: {base: '16px 48px', lg: '16px 48px'},
            height: '54px',
            fontSize: {base: '14px', lg: '16px'},
            fontWeight: '600',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          },
          '& .chakra-link .chakra-text': {
            fontSize: {base: '14px', lg: '16px'},
            fontStyle: 'normal',
            fontWeight: '600',
            lineHeight: '140%',
          },
        },
      },
    }),

    /**
     * Hero Banner Block (CF-01)
     * Full-width hero with background image/video and overlay content
     *
     * @param {Object} args - Component arguments
     * @param {Contentful.HeroBanner} args.component - HeroBanner component data
     * @param {boolean} [args.isComplete] - Whether component has all fields
     * @param {boolean} [args.isFetching] - Whether additional data is being fetched
     *
     * @returns {HeroBannerStyles} - Component styles
     */
    [HERO_BANNER_APPEARANCE.CF_01]: ({component}) => {
      const showBackgroundGradient = isHeroBackgroundGradientEnabled(component);

      return {
        wrapper: {
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          minHeight: {base: '500px', md: '600px', lg: '720px'},
          sx: heroCf01VideoNoContentMobileSx,
        },

        backgroundWrapper: createHeroBackgroundWrapper(
          'linear-gradient(90deg, rgba(0, 0, 0, 0.40) 0%, rgba(0, 0, 0, 0.00) 100%)',
          showBackgroundGradient
        ),

        backgroundMedia: heroBackgroundMedia,

        contentWrapperOuter: {
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          width: '100%',
          maxWidth: {base: '100%', lg: '1440px'},
          marginX: 'auto',
          height: '100%',
        },
        contentWrapper: {
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          height: '100%',
          gap: {base: '18px', md: '20px'},
          minHeight: {base: '605px', md: '632px', lg: '720px'},
          padding: {base: '40px 20px', md: '60px 40px', lg: '80px 76px'},
          maxWidth: {base: '100%', md: '600px', lg: '700px'},
        },
        title: {
          color: 'white',
          fontFamily: 'heading',
          fontSize: {base: '40px', lg: '60px'},
          fontWeight: '400',
          lineHeight: {base: '110%', lg: '120%'},
          letterSpacing: {base: '-0.04px', lg: '-0.06px'},
          margin: 0,
        },

        subtitle: {
          color: 'white',
          fontSize: {base: '14px', lg: '16px'},
          fontWeight: '400',
          lineHeight: '160%',
          margin: 0,
        },

        actionLinks: {
          marginTop: {base: '8px', lg: '12px'},
          sx: {
            'a, button': {
              fontSize: '14px',
              fontWeight: '400',
              lineHeight: '120%',
              padding: {base: '14px 48px', lg: '16px 48px'},
            },
          },
        },
      };
    },

    /**
     * Hero Banner Block (CF-04)
     * Full-viewport hero: background image/video or Background Colour, title + optional Heading Image, subtitle, bottom bar with CTAs.
     * Layout: Figma 3132-2136 / 3132-6326 (title hero); Figma 11830-62006 (freeform: logo + two buttons side by side, 32px gap to logo, 16px between buttons).
     *
     * @param {Object} args - Component arguments
     * @param {Contentful.HeroBanner} args.component - HeroBanner component data
     * @returns {HeroBannerStyles} - Component styles
     */
    [HERO_BANNER_APPEARANCE.CF_04]: ({component}) => {
      const raw = typeof component?.textColour === 'string' ? component.textColour.trim() : '';
      const copyColour = raw ? (raw.startsWith('#') ? raw : `#${raw}`) : 'white';
      const showBackgroundGradient = isHeroBackgroundGradientEnabled(component);

      return {
        root: {
          padding: 0,
        },
        // Figma Desktop 3132-2136: 1440×810; Mobile 3132-6326: height 632
        wrapper: {
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          minHeight: {base: '500px', md: '632px', lg: '810px'},
          sx: heroCf04VideoMobileSx,
        },

        backgroundWrapper: createHeroBackgroundWrapper(
          'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 100%)',
          showBackgroundGradient
        ),

        backgroundMedia: heroBackgroundMedia,

        contentWrapper: {
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          flex: 1,
          // Figma Desktop 3132-2136: content x 128px; Mobile 3132-6326: x 20px, padding bottom 32px
          padding: {base: '32px 20px', lg: '40px 128px'},
          paddingBottom: {base: '32px', lg: '80px'},
          // Figma: desktop gap 32px, mobile 10px between title and CTAs
          gap: {base: '10px', lg: '32px'},
          maxWidth: {base: '100%', lg: '1440px'},
          marginX: 'auto',
          width: '100%',
        },

        // Figma Desktop: Heading 60 (60px, Aktiv Grotesk 400, line-height 1.2em, letter-spacing -1%); Mobile: 40px, line-height 1em
        title: {
          color: copyColour,
          fontFamily: 'aktiv-grotesk, sans-serif',
          fontWeight: '400',
          fontSize: {base: '40px', lg: '60px'},
          lineHeight: {base: '1em', lg: '1.2em'},
          letterSpacing: '-0.01em',
          margin: 0,
          marginBottom: 0,
        },

        // Figma 11830-62006: gap between logo and CTAs = 32px (contentWrapper gap only; no extra margin)
        headingIcon: {
          marginTop: {base: '8px', lg: '12px'},
          marginBottom: {base: '10px', lg: 0},
          maxWidth: '100%',
          sx: {
            'img, svg': {
              display: 'block',
              height: 'auto',
              maxWidth: '100%',
            },
          },
        },

        subtitle: {
          color: copyColour,
          fontSize: {base: '14px', lg: '16px'},
          fontWeight: '400',
          lineHeight: '150%',
          margin: 0,
        },

        // Figma 11830-62006: two buttons side by side below logo; gap 16px; mobile: bar with blur
        bottomBar: {
          position: 'relative',
          zIndex: 3,
          display: 'flex',
          flexDirection: {base: 'column', lg: 'row'},
          alignItems: {base: 'stretch', lg: 'center'},
          justifyContent: {base: 'center', lg: 'flex-start'},
          width: '100%',
          padding: {base: '8px 12px', lg: '0'},
          gap: {base: '8px', lg: '16px'},
          flexWrap: {base: 'wrap', lg: 'nowrap'},
          borderRadius: {base: '4px', lg: 0},
          background: {
            base: 'linear-gradient(0deg, rgba(27, 26, 24, 0.2) 0%, rgba(27, 26, 24, 0.2) 100%), rgba(255, 255, 255, 0.1)',
            lg: 'transparent',
          },
          backdropFilter: {base: 'blur(20px)', lg: 'none'},
        },

        leftCta: {
          display: 'flex',
          alignItems: 'center',
          gap: {base: '16px', lg: '32px'},
          flexWrap: 'wrap',
          width: {base: '100%', lg: 'auto'},

          sx: {
            // Style navigation links; mobile: text center
            'a, button': {
              color: 'white',
              fontSize: {base: '13px', lg: '13px'},
              fontWeight: '500',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              textAlign: {base: 'center', lg: 'left'},
              _hover: {
                textDecoration: 'underline',
              },
            },
            '.chakra-link': {
              fontSize: {base: '13px', lg: '13px'},
              fontWeight: '500',
              color: 'white',
              borderColor: 'transparent',
              backgroundColor: 'transparent',
              paddingLeft: '0',
              paddingRight: '0',
              _hover: {
                color: 'white',
                textDecoration: 'underline',
                backgroundColor: 'transparent',
                borderColor: 'transparent',
              },
            },
            // When both CTAs are in one Action Links (same container), Filled Dark must override .chakra-link transparent
            // States aligned with Design System (styling-check): default, hover, active, disabled
            // KINDW-1012: default label/border #32312F (primaryText), not brandColors.greyscale (#1B1A18); 14px on mobile & desktop per KL-Designs / design sign-off
            'a.chakra-link.link--filled-dark, button.chakra-link.link--filled-dark': {
              backgroundColor: 'white',
              color: 'brandColors.primaryText',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'brandColors.primaryText',
              fontSize: '14px',
              fontWeight: 600,
              lineHeight: '120%',
              padding: {base: '16px', lg: '16px 32px'},
              minHeight: '54px',
              width: {base: '100%', lg: 'auto'},
              justifyContent: 'center',
              textAlign: {base: 'center', lg: 'center'},
              transition: 'all 0.2s ease',
              textDecoration: 'none',
              '& .chakra-text, & p': {
                fontSize: '14px',
                fontWeight: 600,
                lineHeight: '120%',
                color: 'inherit',
              },
              '& img': { transition: 'filter 0.2s ease' },
              _hover: {
                backgroundColor: 'brandColors.greyscale',
                color: 'white',
                borderColor: 'white',
                textDecoration: 'none',
                '& img': { filter: 'brightness(0) invert(1)' },
              },
              _active: {
                backgroundColor: 'brandColors.greyscale',
                color: 'white',
                borderColor: 'white',
                textDecoration: 'none',
                '& img': { filter: 'brightness(0) invert(1)' },
              },
              _disabled: {
                backgroundColor: 'brandColors.greyscale20',
                color: 'brandColors.greyscale40',
                borderColor: 'brandColors.greyscale20',
                cursor: 'not-allowed',
                opacity: 1,
              },
            },
            // Outlined Dark – aligned with Design System (styling-check): default, hover, active, disabled
            '.link--outlined-dark': {
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'white',
              backgroundColor: 'transparent',
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
              lineHeight: '120%',
              padding: {base: '16px', lg: '16px 48px'},
              minHeight: '54px',
              width: {base: '100%', lg: 'auto'},
              justifyContent: 'center',
              textAlign: {base: 'center', lg: 'center'},
              transition: 'all 0.2s ease',
              textDecoration: 'none',
              '& .chakra-text, & p': {
                fontSize: '14px',
                fontWeight: 600,
                lineHeight: '120%',
                color: 'inherit',
              },
              _hover: {
                backgroundColor: 'white',
                color: 'brandColors.primaryText',
                borderColor: 'white',
                textDecoration: 'none',
              },
              _active: {
                backgroundColor: 'white',
                color: 'brandColors.primaryText',
                borderColor: 'white',
                textDecoration: 'none',
              },
              _disabled: {
                backgroundColor: 'brandColors.greyscale',
                color: 'whiteAlpha.450',
                borderColor: 'whiteAlpha.450',
                cursor: 'not-allowed',
                opacity: 1,
              },
            },
            // Mobile: stack buttons vertically (2 lines); desktop: horizontal
            '.chakra-stack': {
              flexDirection: {base: 'column', lg: 'row'},
              alignItems: 'center',
              gap: {base: '8px', lg: '32px'},
            },
          },
        },

        rightCta: {
          display: 'flex',
          alignItems: 'center',
          gap: {base: '12px', lg: '16px'},
          marginLeft: 0,
          marginRight: 0,
          width: {base: '100%', lg: 'auto'},

          sx: {
            // Style action buttons (default: white text for outlined on dark bar); mobile: full-width, text center
            'a, button': {
              alignItems: 'center',
              color: 'white',
              display: 'inline-flex',
              fontSize: '14px',
              fontWeight: '600',
              padding: {base: '14px 48px', lg: '14px 48px'},
              gap: '8px',
              whiteSpace: 'nowrap',
              width: {base: '100%', lg: 'auto'},
              justifyContent: {base: 'center', lg: 'flex-start'},
              textAlign: {base: 'center', lg: 'left'},
            },
            // Filled Dark – aligned with Design System (styling-check): default, hover, active, disabled
            'a.link--filled-dark, button.link--filled-dark': {
              backgroundColor: 'white',
              color: 'brandColors.primaryText',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'brandColors.primaryText',
              fontSize: '14px',
              fontWeight: 600,
              lineHeight: '120%',
              padding: {base: '16px', lg: '16px 32px'},
              minHeight: '54px',
              width: {base: '100%', lg: 'auto'},
              justifyContent: 'center',
              textAlign: {base: 'center', lg: 'left'},
              transition: 'all 0.2s ease',
              textDecoration: 'none',
              '& .chakra-text, & p': {
                fontSize: '14px',
                fontWeight: 600,
                lineHeight: '120%',
                color: 'inherit',
              },
              '& img': { transition: 'filter 0.2s ease' },
              _hover: {
                backgroundColor: 'brandColors.greyscale',
                color: 'white',
                borderColor: 'white',
                textDecoration: 'none',
                '& img': { filter: 'brightness(0) invert(1)' },
              },
              _active: {
                backgroundColor: 'brandColors.greyscale',
                color: 'white',
                borderColor: 'white',
                textDecoration: 'none',
                '& img': { filter: 'brightness(0) invert(1)' },
              },
              _disabled: {
                backgroundColor: 'brandColors.greyscale20',
                color: 'brandColors.greyscale40',
                borderColor: 'brandColors.greyscale20',
                cursor: 'not-allowed',
                opacity: 1,
              },
            },
            '.link--base': {
              fontSize: {base: '13px', lg: '13px'},
              fontWeight: '500',
              padding: '14px 16px',
            },
            // Mobile: stack buttons vertically (2 lines); desktop: horizontal
            '.chakra-stack': {
              flexDirection: {base: 'column', lg: 'row'},
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: {base: '8px', lg: '16px'},
            },
          },
        },
      };
    },

    /**
     * Hero Banner Block (CF-06)
     * Background image/video on top with content section below (title left, subtitle right)
     *
     * @param {Object} args - Component arguments
     * @param {Contentful.HeroBanner} args.component - HeroBanner component data
     * @param {boolean} [args.isComplete] - Whether component has all fields
     * @param {boolean} [args.isFetching] - Whether additional data is being fetched
     *
     * @returns {HeroBannerStyles} - Component styles
     */
    [HERO_BANNER_APPEARANCE.CF_06]: () => {
      return {
        wrapper: {
          position: 'relative',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          sx: heroCf06NoContentSx,
        },

        mediaWrapper: createHeroMediaWrapper({
          aspectRatio: {base: '375/468', lg: '1440/535'},
        }),

        backgroundMedia: heroBackgroundMedia,

        contentSection: {
          display: 'flex',
          flexDirection: {base: 'column', lg: 'row'},
          alignItems: 'flex-start',
          gap: {base: '24px', lg: '150px'},
          width: '100%',
          maxWidth: {base: '100%', lg: '1440px'},
          marginX: 'auto',
          px: {base: '20px', lg: '80px'},
          py: {base: '36px', lg: '120px'},
          pb: {base: '36px', lg: '48px'}
        },

        titleColumn: {
          width: {base: '100%', lg: '40%'},
          flexShrink: 0,
        },

        title: {
          color: 'brandColors.greyscale90',
          fontFamily: 'heading',
          fontSize: {base: '40px', lg: '72px'},
          fontWeight: {base: '500', lg: '400'},
          lineHeight: {base: '120%', lg: '100%'},
          letterSpacing: {base: '-0.4px', lg: '-0.72px'},
          margin: 0,
        },

        subtitleColumn: {
          display: 'flex',
          flexDirection: 'column',
          justifyContent: {base: 'center', lg: 'flex-start'},
          alignItems: 'flex-start',
          flex: '1 0 0',
          pt: {base: 0, lg: '14px'},
        },

        subtitle: {
          color: 'brandColors.greyscale90',
          fontSize: {base: '16px', lg: '20px'},
          fontWeight: '400',
          lineHeight: {base: '150%', lg: '160%'},
          margin: 0,
        },
      };
    },

    /**
     * Showrooms Header (CF-032) — layout in ShowroomListHeader theme.
     */
    [HERO_BANNER_APPEARANCE.SHOWROOMS_HEADER_CF_032]: () => ({
      root: {
        width: '100%',
        padding: 0,
      },
    }),
  },
});
